import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Check, Smartphone, Building2, ShieldCheck, Loader2, User, Phone, Briefcase, CheckCircle2, Lock, Wallet, Zap, BadgeCheck, Sparkles, FileText, Printer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SiteShell } from "@/components/site/SiteShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fetchPaymentMethods } from "@/lib/content";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { showSuccessNotification } from "@/components/site/SuccessNotificationPopup";
import { SeoFaqBlock } from "@/components/site/SeoFaqBlock";
import { SEO_CONTENT } from "@/components/site/seo-content";
import { useCampaignDiscount, applyDiscount } from "@/hooks/use-campaign-discount";

export const Route = createFileRoute("/payment")({
  validateSearch: (s: Record<string, unknown>) => {
    const v = typeof s.order === "string" ? s.order.trim() : "";
    return v ? { order: v } : ({} as { order?: string });
  },
  head: () => ({
    meta: [
      { title: "Payment — Online Tax VAT Bangladesh" },
      { name: "description", content: "Submit your payment via bKash, Nagad, Rocket or bank transfer." },
      { name: "robots", content: "noindex, nofollow" },
    ],
    links: [
      { rel: "canonical", href: "https://onlinetaxvat.com/payment" },
    ],
  }),
  component: PaymentPage,
});

type Method = {
  id: string; method: string; account_name?: string; account_number: string;
  account_type?: string; instructions?: string; logo_url?: string; processing_fee?: number;
  bank_name?: string; branch_name?: string; routing_number?: string;
  brand_color?: string | null;
};

type Brand = {
  key: string;
  name: string;
  primary: string;
  soft: string;
  ring: string;
  steps: string[];
};

// Brand color presets, used when admin hasn't set brand_color on a row.
const BRAND_PRESETS: Record<string, string> = {
  bkash: "#D12053",
  nagad: "#F57224",
  rocket: "#8C3494",
  bank: "#1E3A8A",
};

function isValidHex(c?: string | null): c is string {
  return !!c && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(c.trim());
}

function softFromHex(hex: string, alpha = 0.12): string {
  const h = hex.replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function getBrand(method: Pick<Method, "method" | "brand_color"> | undefined): Brand {
  const name = (method?.method || "").toLowerCase();
  const override = isValidHex(method?.brand_color) ? method!.brand_color!.trim() : null;

  let key = "bank";
  let display = method?.method || "Payment";
  let preset = BRAND_PRESETS.bank;
  let steps = [
    "নিচের অ্যাকাউন্টে টাকা পাঠান",
    "রেফারেন্স/TrxID সংগ্রহ করুন",
    "ফর্ম পূরণ করে সাবমিট করুন",
  ];

  if (name.includes("bkash") || name.includes("বিকাশ")) {
    key = "bkash"; display = "bKash"; preset = BRAND_PRESETS.bkash;
    steps = [
      "bKash অ্যাপ খুলুন → Send Money নির্বাচন করুন",
      "নিচের মার্চেন্ট নাম্বারে টাকা পাঠান",
      "TrxID কপি করে নিচের ফর্মে দিন",
    ];
  } else if (name.includes("nagad") || name.includes("নগদ")) {
    key = "nagad"; display = "Nagad"; preset = BRAND_PRESETS.nagad;
    steps = [
      "Nagad অ্যাপ খুলুন → Send Money নির্বাচন করুন",
      "নিচের নাম্বারে টাকা পাঠান",
      "TrxID কপি করে ফর্মে পেস্ট করুন",
    ];
  } else if (name.includes("rocket") || name.includes("রকেট")) {
    key = "rocket"; display = "Rocket"; preset = BRAND_PRESETS.rocket;
    steps = [
      "Rocket অ্যাপ/USSD খুলুন → Send Money",
      "নিচের নাম্বারে টাকা পাঠান",
      "TrxID কপি করে ফর্মে দিন",
    ];
  }

  const primary = override || preset;
  return {
    key, name: display, primary,
    soft: softFromHex(primary, 0.14),
    ring: softFromHex(primary, 0.30),
    steps,
  };
}

function PaymentPage() {
  const { t } = useTranslation();
  const { user, loading: authLoading } = useAuth();
  const { order: orderId = "" } = Route.useSearch();
  const [methods, setMethods] = useState<Method[]>([]);
  const [order, setOrder] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const [methodId, setMethodId] = useState<string>("");
  const [senderNumber, setSenderNumber] = useState("");
  const [trxIdInput, setTrxIdInput] = useState("");
  const [notes, setNotes] = useState("");
  const [manualAmount, setManualAmount] = useState<string>("");
  const [taxAmountInput, setTaxAmountInput] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{
    trx: string;
    method: string;
    senderNumber: string;
    amount: number;
    fee: number;
    tax: number;
    govt: number;
    total: number;
    accountNumber: string;
    accountName?: string;
    refId?: string;
    serviceName: string;
    customerName: string;
    customerPhone: string;
    paidAt: string;
  } | null>(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedWallet, setCopiedWallet] = useState(false);

  useEffect(() => { fetchPaymentMethods().then((m) => setMethods(m as Method[])); }, []);
  useEffect(() => {
    if (!orderId) return;
    supabase.from("orders").select("*").eq("id", orderId).maybeSingle().then(({ data }) => {
      setOrder(data);
      const presetTax = Number((data as any)?.tax_amount ?? 0);
      if (presetTax > 0) setTaxAmountInput(String(presetTax));
    });
  }, [orderId]);
  useEffect(() => {
    if (!user) return;
    supabase.from("profiles").select("*").eq("id", user.id).maybeSingle().then(({ data }) => setProfile(data));
  }, [user]);

  const selected = useMemo(() => methods.find((m) => m.id === methodId) || null, [methods, methodId]);
  const packagePrice = Number(order?.total_price ?? order?.amount ?? 0);
  const orderAmount = Number(order?.amount ?? 0);
  const presetOrderTax = Number(order?.tax_amount ?? 0);
  const presetOrderGovt = Number(order?.govt_charge_fee ?? 0);
  // If we have a locked package price, prefer it; otherwise fall back to the legacy manual-amount flow
  const hasLockedPrice = packagePrice > 0;
  const needsManualAmount = !hasLockedPrice && (!orderAmount || orderAmount <= 0);
  // Subtract any pre-set tax/govt-charge baked into total_price so we don't double-count
  const rawBase = hasLockedPrice ? (packagePrice - presetOrderTax - presetOrderGovt) : (needsManualAmount ? Number(manualAmount || 0) : orderAmount);
  const baseAmountOriginal = Math.max(0, rawBase);
  const campaign = useCampaignDiscount();
  // Discount applies only to package base; never to tax/govt/processing.
  const { discount: discountAmount, discounted: baseAmount } = applyDiscount(
    hasLockedPrice ? baseAmountOriginal : 0,
    campaign,
  );
  const effectiveBase = hasLockedPrice ? baseAmount : baseAmountOriginal;
  const fee = Number(selected?.processing_fee ?? 0);
  const taxAmount = Math.max(0, Number(taxAmountInput || 0));
  const govtChargeFee = Math.round(taxAmount * 0.01 * 100) / 100;
  const total = effectiveBase + fee + taxAmount + govtChargeFee;
  const lockedMinTotal = hasLockedPrice
    ? (packagePrice - discountAmount) // discount deducted from locked price
    : 0;


  const customerName = order?.customer_name || profile?.full_name || user?.user_metadata?.full_name || "—";
  const customerPhone = order?.customer_phone || profile?.phone || "—";
  const serviceName = order?.service_title ? `${order.service_title} · ${order.package_name}` : t("paymentPage.customPayment");

  function copyWallet() {
    if (!selected) return;
    navigator.clipboard.writeText(selected.account_number);
    setCopiedWallet(true); setTimeout(() => setCopiedWallet(false), 1500);
  }
  function copyTrx() {
    if (!done) return;
    navigator.clipboard.writeText(done.trx);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    if (!user) { setSubmitting(false); return; }
    if (!selected) { setSubmitting(false); return toast.error(t("paymentPage.selectMethod")); }
    if (needsManualAmount && (!baseAmount || baseAmount <= 0)) { setSubmitting(false); return toast.error(t("paymentPage.enterAmountErr")); }

    // Enforce locked (possibly discounted) package price for normal customers
    if (hasLockedPrice && total < lockedMinTotal) {
      setSubmitting(false);
      return toast.error(
        `আপনার পরিশোধযোগ্য সর্বনিম্ন পরিমাণ ${lockedMinTotal.toLocaleString()} টাকা। এর চেয়ে কম পেমেন্ট করা যাবে না।`,
      );
    }

    if (!/^01[0-9]{9}$/.test(senderNumber)) { setSubmitting(false); return toast.error(t("paymentPage.invalidPhone")); }
    if (!trxIdInput.trim()) { setSubmitting(false); return toast.error(t("paymentPage.enterTrx")); }
    const refTrx = trxIdInput.trim().toUpperCase();
    const payload = {
      payment_method: selected.method,
      payment_sender_number: senderNumber,
      payment_transaction_id: refTrx,
      payment_status: "pending_verification",
      amount: total,
      tax_amount: taxAmount,
      govt_charge_fee: govtChargeFee,
    };
    let err;
    if (orderId) {
      ({ error: err } = await supabase.from("orders").update(payload).eq("id", orderId));
    } else {
      const ref = `PAY-${Date.now().toString(36).toUpperCase()}`;
      ({ error: err } = await supabase.from("orders").insert({
        ...payload,
        user_id: user.id,
        reference_id: ref,
        service_slug: "direct-payment",
        service_title: "Direct Payment",
        package_slug: "custom",
        package_name: "Custom",
        total_price: total,
        status: "pending",
        customer_name: customerName,
        customer_phone: customerPhone,
        notes: notes || null,
      }));
    }
    setSubmitting(false);
    if (err) {
      // Log full error for debugging permission / RLS issues
      console.error("[payment.submit] order write failed", {
        code: (err as any).code,
        message: err.message,
        details: (err as any).details,
        hint: (err as any).hint,
        orderId,
        userId: user.id,
      });
      const msg = err.message || "";
      const isPerm = /permission denied|row-level security|policy/i.test(msg) || (err as any).code === "42501";
      if (isPerm) {
        toast.error("Payment could not be saved — permission denied. Please sign out and sign in again, then retry. If the problem persists, contact support.");
      } else {
        toast.error(msg || "Payment submission failed. Please try again.");
      }
      return;
    }
    setDone({
      trx: refTrx,
      method: selected.method,
      senderNumber,
      amount: effectiveBase,
      fee,
      tax: taxAmount,
      govt: govtChargeFee,
      total,
      accountNumber: selected.account_number,
      accountName: selected.account_name,
      refId: order?.reference_id,
      serviceName,
      customerName,
      customerPhone,
      paidAt: new Date().toLocaleString("en-GB", { hour12: true }),
    });
    toast.success(t("paymentPage.toastSubmitted"));
    showSuccessNotification({
      title: "✅ Payment Successful",
      message: `TrxID: ${refTrx} — আপনার পেমেন্ট সফলভাবে সাবমিট হয়েছে। ভেরিফাই হলে SMS পাবেন।`,
      duration: 5000,
    });
  }

  const accent = selected ? getBrand(selected).primary : "#0f172a";

  if (authLoading || !user) {
    return <SiteShell><div className="container-page py-24 max-w-md text-center" style={{ contain: "layout style" }}>
      <h1 className="font-display text-2xl font-bold home-hero-title">{t("paymentPage.signinRequired")}</h1>
      <div className="mt-6 flex justify-center" style={{ minHeight: 44 }}>
        {authLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" aria-hidden />
        ) : (
          <Button asChild><Link to="/login">{t("paymentPage.signin")}</Link></Button>
        )}
      </div>
    </div></SiteShell>;
  }

  return (
    <SiteShell>
      <style>{`
        .payment-page, .payment-page * {
          font-family: 'Hind Siliguri', 'Noto Sans Bengali', 'SF Pro Text', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
          letter-spacing: 0;
          font-kerning: normal;
        }
        header > div > nav.glass {
          background: #22c55e !important;
          color: #ffffff !important;
          border-color: rgba(255,255,255,0.25) !important;
        }
        header > div > nav.glass a,
        header > div > nav.glass button,
        header > div > nav.glass span { color: #ffffff !important; }
        .payment-page input,
        .payment-page textarea,
        .payment-page [role="combobox"] {
          color: #0f172a !important;
          -webkit-text-fill-color: #0f172a !important;
          background-color: #ffffff !important;
          caret-color: #0f172a;
          color-scheme: light;
        }
        .payment-page input::placeholder,
        .payment-page textarea::placeholder {
          color: #64748b !important;
          -webkit-text-fill-color: #64748b !important;
          opacity: 1;
        }
        .payment-page input:-webkit-autofill,
        .payment-page textarea:-webkit-autofill {
          -webkit-text-fill-color: #0f172a !important;
          box-shadow: 0 0 0 1000px #ffffff inset !important;
        }
        /* Readability: deepen muted greys, solidify whites */
        .payment-page .text-slate-400 { color: #334155 !important; }
        .payment-page .text-slate-500 { color: #1f2937 !important; }
        .payment-page .text-slate-600 { color: #0f172a !important; }
        .payment-page .text-slate-700,
        .payment-page .text-slate-900 { color: #000000 !important; }
        .payment-page .text-white,
        .payment-page .text-white\\/80,
        .payment-page .text-white\\/90 { color: #ffffff !important; opacity: 1 !important; }
        .payment-page .text-muted-foreground { color: #1f2937 !important; }
      `}</style>

      <div className="fixed inset-0 -z-10 bg-white" />
      <div
        aria-hidden
        className="fixed inset-x-0 top-0 -z-10 h-[520px] pointer-events-none"
        style={{
          background:
            `radial-gradient(60% 60% at 20% 0%, ${accent}1a 0%, transparent 60%),` +
            `radial-gradient(50% 50% at 85% 10%, #10b9811f 0%, transparent 60%),` +
            `linear-gradient(180deg, #f8fafc 0%, #ffffff 70%)`,
        }}
      />
      <section className="payment-page relative pt-10 md:pt-16 pb-20 z-10">
        <div className="container-page">
          <div className="text-center max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="inline-flex items-center gap-2 text-xs font-semibold text-slate-600 px-3.5 py-1.5 rounded-full border border-slate-200 bg-white/80 backdrop-blur shadow-sm"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" style={{ background: accent }} />
                <span className="relative inline-flex h-2 w-2 rounded-full" style={{ background: accent }} />
              </span>
              {t("paymentPage.eyebrow")}
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="mt-5 font-display text-2xl sm:text-3xl md:text-5xl font-bold leading-[1.15] tracking-tight text-slate-900 break-words"
            >
              {t("paymentPage.titleA")}{t("paymentPage.titleB")}
            </motion.h1>
            <p className="mt-4 text-sm md:text-base text-slate-500 max-w-xl mx-auto leading-relaxed">{t("paymentPage.desc")}</p>
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="mt-6 flex flex-wrap items-center justify-center gap-2"
            >
              {[
                {
                  label: "256-bit SSL Encrypted",
                  ring: "ring-emerald-200",
                  bg: "from-emerald-500 to-green-600",
                  text: "text-emerald-700",
                  icon: (
                    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 text-white" strokeWidth={2.4} stroke="currentColor">
                      <rect x="4" y="11" width="16" height="9" rx="2" fill="currentColor" fillOpacity="0.15" />
                      <path d="M8 11V8a4 4 0 1 1 8 0v3" stroke="currentColor" strokeLinecap="round" />
                      <path d="M12 15v2" stroke="currentColor" strokeLinecap="round" />
                    </svg>
                  ),
                  dot: true,
                },
                {
                  label: "PCI-compliant flow",
                  ring: "ring-sky-200",
                  bg: "from-sky-500 to-blue-600",
                  text: "text-sky-700",
                  icon: (
                    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 text-white" strokeWidth={2.4} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2.5 4 5.5v6c0 4.5 3.4 8.6 8 10 4.6-1.4 8-5.5 8-10v-6L12 2.5Z" fill="currentColor" fillOpacity="0.18" />
                      <path d="m8.5 12 2.5 2.5L15.5 10" />
                    </svg>
                  ),
                },
                {
                  label: "NBR Verified",
                  ring: "ring-rose-200",
                  bg: "from-rose-500 to-red-600",
                  text: "text-rose-700",
                  icon: (
                    <svg viewBox="0 0 24 24" fill="none" className="h-4 w-4 text-white" strokeWidth={2.4} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2 3.5 6v5c0 5 3.6 9.7 8.5 11 4.9-1.3 8.5-6 8.5-11V6L12 2Z" fill="currentColor" fillOpacity="0.18" />
                      <path d="m8.5 12.2 2.4 2.4L15.6 9.8" />
                    </svg>
                  ),
                },
              ].map((b, i) => (
                <span
                  key={i}
                  className={`inline-flex items-center gap-2 text-[12px] font-semibold ${b.text} pl-1 pr-3 py-1 rounded-full bg-white/95 border border-slate-200 shadow-[0_4px_14px_-6px_rgba(15,23,42,0.18)] ring-1 ${b.ring}`}
                >
                  <span className={`relative inline-flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br ${b.bg} shadow-[inset_0_-1px_2px_rgba(0,0,0,0.15)] ring-1 ring-white/40`}>
                    {b.icon}
                    {b.dot && (
                      <span className="absolute -top-0.5 -right-0.5 inline-flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500 ring-1 ring-white" />
                      </span>
                    )}
                  </span>
                  {b.label}
                </span>
              ))}
            </motion.div>
          </div>


          <div className="mt-10 grid lg:grid-cols-5 gap-6">
            {/* LEFT — Billing */}
            <div className="lg:col-span-2 space-y-4">
              <Panel>
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-2 w-2 rounded-full" style={{ background: accent }} />
                  <h3 className="font-display font-bold text-lg text-slate-900">{t("paymentPage.billing")}</h3>
                </div>
                <Row icon={<User className="h-4 w-4" />} label={t("paymentPage.paidBy")} value={customerName} />
                <Row icon={<Phone className="h-4 w-4" />} label={t("paymentPage.contactNo")} value={customerPhone} />
                <Row icon={<Briefcase className="h-4 w-4" />} label={t("paymentPage.paymentTo")} value={serviceName} />
                {order?.reference_id && <Row icon={<ShieldCheck className="h-4 w-4" />} label={t("paymentPage.orderRef")} value={order.reference_id} mono />}
              </Panel>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h4 className="font-semibold flex items-center gap-2 text-slate-900">
                  <Lock className="h-4 w-4 text-emerald-600" /> {t("paymentPage.whySafe")}
                </h4>
                <ul className="mt-3 space-y-2.5 text-sm">
                  {[t("paymentPage.safe1"), t("paymentPage.safe2"), t("paymentPage.safe3")].map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-slate-600">
                      <ShieldCheck className="mt-0.5 h-3.5 w-3.5 text-emerald-500 shrink-0" />
                      <span className="leading-relaxed">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* RIGHT — form */}
            <div className="lg:col-span-3">
              <Panel>
                {done ? (
                  <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="relative text-center py-8 overflow-hidden">
                    {/* Confetti dots */}
                    {Array.from({ length: 14 }).map((_, i) => (
                      <motion.span
                        key={i}
                        initial={{ opacity: 0, y: 0, x: 0, scale: 0 }}
                        animate={{
                          opacity: [0, 1, 0],
                          y: [0, -40 - Math.random() * 80],
                          x: [(Math.random() - 0.5) * 240],
                          scale: [0, 1, 0.6],
                        }}
                        transition={{ duration: 1.6, delay: 0.2 + i * 0.04, ease: "easeOut" }}
                        className="absolute left-1/2 top-1/2 h-2 w-2 rounded-full"
                        style={{ background: ["#10b981", "#22c55e", "#0ea5e9", "#f59e0b", "#e2136e"][i % 5] }}
                      />
                    ))}
                    <motion.div
                      initial={{ scale: 0, rotate: -30 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 220, damping: 14, delay: 0.1 }}
                      className="mx-auto w-24 h-24 rounded-full flex items-center justify-center bg-gradient-to-br from-emerald-50 to-emerald-100 ring-4 ring-emerald-100 relative"
                    >
                      <CheckCircle2 className="h-12 w-12 text-emerald-500" />
                      <motion.div
                        initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.5 }}
                        className="absolute -right-2 -top-2 bg-white rounded-full p-1 shadow-md"
                      >
                        <Sparkles className="h-4 w-4 text-amber-500" />
                      </motion.div>
                    </motion.div>

                    {/* Animated PAID stamp */}
                    <motion.div
                      initial={{ scale: 1.6, opacity: 0, rotate: -18 }}
                      animate={{ scale: 1, opacity: 1, rotate: -12 }}
                      transition={{ delay: 0.6, type: "spring", stiffness: 180, damping: 12 }}
                      className="absolute right-4 top-4 sm:right-8 sm:top-6 select-none pointer-events-none"
                    >
                      <div className="font-display font-black text-xl sm:text-2xl px-4 py-1.5 border-[3px] border-emerald-500 text-emerald-600 rounded-md tracking-widest opacity-90 bg-white/60">
                        PAID
                      </div>
                    </motion.div>

                    <h3 className="mt-5 font-display font-bold text-2xl sm:text-3xl text-slate-900">Payment Successful</h3>
                    <p className="text-sm text-slate-500 mt-1.5 max-w-xs mx-auto">{t("paymentPage.queued")}</p>

                    <div className="mt-7 mx-auto max-w-sm rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/60 to-white p-5 shadow-sm">
                      <div className="flex items-center justify-center gap-1.5 text-[11px] font-semibold text-emerald-700 uppercase tracking-wider">
                        <BadgeCheck className="h-3.5 w-3.5" /> Receipt · Reference
                      </div>
                      <p className="mt-2 font-mono text-2xl font-bold text-slate-900 tracking-wide break-all">{done.trx}</p>
                      <div className="mt-3 flex items-center justify-center">
                        <Button size="sm" variant="outline" onClick={copyTrx}>
                          {copied ? <><Check className="h-3.5 w-3.5 mr-1" /> {t("common.copied")}</> : <><Copy className="h-3.5 w-3.5 mr-1" /> {t("common.copy")}</>}
                        </Button>
                      </div>
                      <div className="mt-4 pt-3 border-t border-dashed border-emerald-200 text-[11px] text-slate-500 flex items-center justify-center gap-1.5">
                        <Lock className="h-3 w-3" /> Securely logged · ভেরিফাই হলে SMS পাবেন
                      </div>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-3 justify-center">
                      <Button
                        onClick={() => setInvoiceOpen(true)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      >
                        <FileText className="h-4 w-4 mr-1.5" /> Payment Invoice দেখুন
                      </Button>
                      <Button asChild variant="outline"><Link to="/dashboard">{t("paymentPage.viewDashboard")}</Link></Button>
                      <Button asChild variant="ghost"><Link to="/">{t("paymentPage.done")}</Link></Button>
                    </div>

                    {/* Invoice dialog */}
                    <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
                      <DialogContent className="max-w-md p-0 overflow-hidden bg-white">
                        <div id="payment-invoice-print" className="text-left">
                          <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 px-6 py-5 text-white">
                            <DialogHeader>
                              <DialogTitle className="text-white text-xl font-bold flex items-center gap-2">
                                <BadgeCheck className="h-5 w-5" /> Payment Invoice
                              </DialogTitle>
                            </DialogHeader>
                            <p className="text-emerald-50 text-xs mt-1">Online Tax VAT Bangladesh</p>
                          </div>
                          <div className="px-6 py-5 space-y-4">
                            <div className="flex items-center justify-between pb-3 border-b border-dashed border-slate-200">
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Transaction ID</p>
                                <p className="font-mono font-bold text-slate-900 text-base break-all">{done.trx}</p>
                              </div>
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                                <CheckCircle2 className="h-3 w-3" /> Paid
                              </span>
                            </div>

                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Payment Method</p>
                                <p className="font-semibold text-slate-900 capitalize">{done.method}</p>
                              </div>
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Paid From</p>
                                <p className="font-semibold text-slate-900 font-mono">{done.senderNumber}</p>
                              </div>
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Paid To</p>
                                <p className="font-semibold text-slate-900 font-mono">{done.accountNumber}</p>
                              </div>
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Date</p>
                                <p className="font-semibold text-slate-900">{done.paidAt}</p>
                              </div>
                            </div>

                            <div className="pt-3 border-t border-dashed border-slate-200 space-y-1.5 text-sm">
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Customer</p>
                                <p className="font-semibold text-slate-900">{done.customerName} · {done.customerPhone}</p>
                              </div>
                              <div>
                                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Service</p>
                                <p className="font-semibold text-slate-900">{done.serviceName}</p>
                              </div>
                              {done.refId && (
                                <div>
                                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Order Ref</p>
                                  <p className="font-semibold text-slate-900 font-mono">{done.refId}</p>
                                </div>
                              )}
                            </div>

                            <div className="pt-3 border-t border-dashed border-slate-200 space-y-1.5">
                              <div className="flex justify-between text-sm text-slate-600">
                                <span>Amount</span>
                                <span className="font-medium">৳ {done.amount.toLocaleString()}</span>
                              </div>
                              {done.fee > 0 && (
                                <div className="flex justify-between text-sm text-slate-600">
                                  <span>Processing Fee</span>
                                  <span className="font-medium">৳ {done.fee.toLocaleString()}</span>
                                </div>
                              )}
                              {done.tax > 0 && (
                                <div className="flex justify-between text-sm text-slate-600">
                                  <span>Tax</span>
                                  <span className="font-medium">৳ {done.tax.toLocaleString()}</span>
                                </div>
                              )}
                              {done.govt > 0 && (
                                <div className="flex justify-between text-sm text-slate-600">
                                  <span>Govt Charge</span>
                                  <span className="font-medium">৳ {done.govt.toLocaleString()}</span>
                                </div>
                              )}
                              <div className="flex justify-between text-base font-bold text-slate-900 pt-2 border-t border-slate-200">
                                <span>Total Paid</span>
                                <span className="text-emerald-700">৳ {done.total.toLocaleString()}</span>
                              </div>
                            </div>

                            <div className="text-center text-[11px] text-slate-400 pt-3 border-t border-dashed border-slate-200">
                              ধন্যবাদ! ভেরিফাই হলে আপনি SMS পাবেন।
                            </div>
                          </div>
                        </div>
                        <div className="px-6 py-3 bg-slate-50 flex gap-2 justify-end border-t border-slate-200 print:hidden">
                          <Button variant="outline" size="sm" onClick={() => window.print()}>
                            <Printer className="h-3.5 w-3.5 mr-1.5" /> Print
                          </Button>
                          <Button size="sm" onClick={() => setInvoiceOpen(false)}>Close</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </motion.div>

                ) : (
                  <form onSubmit={submit} className="space-y-5">
                    <div>
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("paymentPage.method")}</Label>
                        {selected && (
                          <button type="button" onClick={() => setMethodId("")} className="text-[11px] font-semibold text-slate-500 hover:text-slate-900 transition-colors">
                            Change
                          </button>
                        )}
                      </div>

                      {!methodId && (
                        <div className="mt-2 grid grid-cols-2 gap-2.5">
                          {methods.map((m) => {
                            const b = getBrand(m);
                            return (
                              <motion.button
                                key={m.id}
                                type="button"
                                onClick={() => setMethodId(m.id)}
                                whileHover={{ y: -3 }}
                                whileTap={{ scale: 0.98 }}
                                className="group relative rounded-2xl p-4 text-left overflow-hidden transition-all"
                                style={{
                                  background: b.primary,
                                  color: "#ffffff",
                                  boxShadow: `0 10px 24px -12px ${b.ring}`,
                                  border: `1px solid ${b.ring}`,
                                }}
                              >

                                <div className="relative flex items-center gap-3">
                                  <div className="shrink-0 flex items-center justify-center overflow-hidden" style={{ width: 56, height: 56 }}>
                                    {m.logo_url ? (
                                      <img src={m.logo_url} alt={`${m.method} payment logo — Online Tax VAT Bangladesh`} loading="lazy" decoding="async" style={{ width: "100%", height: "100%", objectFit: "contain", mixBlendMode: "screen" }} />
                                    ) : m.method?.toLowerCase().includes("bank") ? (
                                      <Building2 className="h-9 w-9 text-white" />
                                    ) : (
                                      <Smartphone className="h-9 w-9 text-white" />
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="font-display font-extrabold text-2xl sm:text-3xl text-white truncate drop-shadow-sm">{m.method}</p>
                                    <p className="text-sm font-bold text-white/90 mt-0.5">
                                      {Number(m.processing_fee ?? 0) === 0
                                        ? "No extra fee"
                                        : `${t("paymentPage.fee")} ৳${Number(m.processing_fee ?? 0)}`}
                                    </p>
                                  </div>
                                </div>
                              </motion.button>
                            );
                          })}
                        </div>
                      )}

                      <AnimatePresence mode="wait">
                        {selected && (() => {
                          const brand = getBrand(selected);
                          return <BrandCard key={brand.key + selected.id} brand={brand} method={selected} onCopy={copyWallet} copied={copiedWallet} />;
                        })()}
                      </AnimatePresence>
                    </div>


                    {hasLockedPrice ? (
                      <Field label="প্যাকেজের মূল্য (লক করা)" hint="এই দাম পরিবর্তন করা যাবে না — পুরো মূল্য পরিশোধ করতে হবে।">
                        <div className="relative">
                          <Input
                            type="text"
                            value={`৳ ${packagePrice.toLocaleString()}`}
                            readOnly
                            disabled
                            className="h-11 font-mono text-slate-900 bg-slate-100 cursor-not-allowed pr-10"
                          />
                          <Lock className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        </div>
                      </Field>
                    ) : needsManualAmount && (
                      <Field label={t("paymentPage.enterAmount")} hint={t("paymentPage.enterAmountHint")}>
                      <Input
                          type="number" min="1"
                          value={manualAmount}
                          onChange={(e) => setManualAmount(e.target.value.replace(/[^\d.]/g, ""))}
                          placeholder={t("paymentPage.amountPlaceholder")}
                          inputMode="decimal"
                          className="h-11 font-mono text-slate-900"
                        />
                      </Field>
                    )}

                    <Field label="ট্যাক্সের পরিমাণ (যদি থাকে)" hint="আপনার সরকারি ট্যাক্স পরিমাণ লিখুন — না থাকলে খালি রাখুন।">
                      <Input
                        type="number" min="0"
                        value={taxAmountInput}
                        onChange={(e) => setTaxAmountInput(e.target.value.replace(/[^\d.]/g, ""))}
                        placeholder="যেমন: 1000"
                        inputMode="decimal"
                        className="h-11 font-mono text-slate-900"
                      />
                    </Field>

                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-[12.5px] leading-relaxed text-amber-900">
                      <span className="font-semibold">⚠️ বিশেষ নোটিশ:</span> সরকারি নিয়ম অনুযায়ী এ-চালান জমা দেওয়ার সময় গভারমেন্ট গেটওয়ে ফি বাবদ অতিরিক্ত ১% চার্জ প্রযোজ্য হয়, যা <span className="font-semibold">'সরকারি চালান ফি'</span> হিসেবে অটোমেটিক যোগ করা হয়েছে।
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <Field label={t("paymentPage.yourPaymentNumber")}>
                        <Input
                          value={senderNumber}
                          onChange={(e) => setSenderNumber(e.target.value.replace(/\D/g, "").slice(0, 11))}
                          placeholder="01XXXXXXXXX" inputMode="numeric"
                          className="h-11 font-mono text-slate-900"
                        />
                      </Field>
                      <Field label={t("paymentPage.trxId")}>
                        <Input
                          value={trxIdInput}
                          onChange={(e) => setTrxIdInput(e.target.value)}
                          placeholder={t("paymentPage.trxPlaceholder")}
                          className="h-11 font-mono uppercase text-slate-900"
                        />
                      </Field>
                    </div>

                    <Field label={t("uploadPage.notes")}>
                      <Textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder={t("paymentPage.notesPlaceholder")}
                        className="resize-none text-slate-900"
                        rows={3}
                      />
                    </Field>

                    {hasLockedPrice && discountAmount > 0 && (
                      <div className="rounded-xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-amber-50 p-4 shadow-sm">
                        <div className="flex items-center justify-center gap-2 mb-3">
                          <Sparkles className="h-4 w-4 text-emerald-600" />
                          <span className="font-display font-bold text-sm md:text-base text-emerald-700">
                            {campaign.percent}% Discount Applied! 🎉
                          </span>
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between text-slate-700">
                            <span>মূল মূল্য</span>
                            <span className="line-through opacity-70">৳ {baseAmountOriginal.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between text-emerald-700 font-semibold">
                            <span>ডিসকাউন্ট ({campaign.percent}%)</span>
                            <span>− ৳ {discountAmount.toLocaleString()}</span>
                          </div>
                          <div className="h-px bg-emerald-300/60 my-1" />
                          <div className="flex justify-between font-display font-bold text-base text-slate-900">
                            <span>চূড়ান্ত মূল্য</span>
                            <span>৳ {baseAmount.toLocaleString()}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-1.5">
                      {effectiveBase > 0 && <Line label="মূল পরিমাণ" value={`৳ ${effectiveBase.toLocaleString()}`} />}
                      <Line label="সরকারি ট্যাক্স" value={`৳ ${taxAmount.toLocaleString()}`} />
                      <Line label="সরকারি চালান ফি (১%)" value={`৳ ${govtChargeFee.toLocaleString()}`} />
                      {fee > 0 && <Line label={t("paymentPage.processingFee")} value={`৳ ${fee.toLocaleString()}`} />}
                      <div className="h-px bg-slate-200 my-1" />
                      <Line label="মোট" value={`৳ ${total.toLocaleString()}`} bold accent={accent} />
                    </div>



                    <motion.button
                      type="submit" disabled={submitting}
                      whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
                      className="relative w-full h-14 rounded-xl font-display font-bold text-base text-white overflow-hidden disabled:opacity-60 transition-shadow"
                      style={{
                        background: accent,
                        boxShadow: `0 12px 30px -10px ${accent}66`,
                      }}
                    >
                      <span className="relative flex items-center justify-center gap-2">
                        {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Zap className="h-4 w-4" /> {t("paymentPage.submit")}</>}
                      </span>
                    </motion.button>

                    <p className="text-[11px] text-slate-500 text-center flex items-center justify-center gap-1">
                      <Lock className="h-3 w-3" /> {t("paymentPage.neverStore")}
                    </p>
                  </form>
                )}
              </Panel>
            </div>
          </div>
          {(() => { const c = SEO_CONTENT["payment"]; return <SeoFaqBlock title={c.title} intro={c.intro} bullets={c.bullets} faqs={c.faqs} />; })()}
        </div>
      </section>
    </SiteShell>
  );
}

/* ---------- Subcomponents ---------- */

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative rounded-2xl border border-slate-200 bg-white p-6 md:p-7 shadow-sm">
      {children}
    </div>
  );
}

function BrandCard({ brand, method, onCopy, copied }: { brand: Brand; method: Method; onCopy: () => void; copied: boolean; }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.98 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="mt-4 relative rounded-2xl overflow-hidden"
      style={{
        background: brand.primary,
        color: "#ffffff",
        border: `1px solid ${brand.ring}`,
        boxShadow: `0 18px 40px -18px ${brand.ring}`,
      }}
    >

      <div className="relative p-5 sm:p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div className="rounded-xl shrink-0 bg-white/15 backdrop-blur-sm border border-white/25" style={{ width: 64, height: 64, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
              {method.logo_url
                ? <img src={method.logo_url} alt={`${brand.name} payment method — Online Tax VAT Bangladesh checkout`} loading="lazy" decoding="async" style={{ width: "100%", height: "100%", objectFit: "contain", padding: 4, mixBlendMode: "screen" }} />
                : <Wallet className="h-7 w-7 text-white" />}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-wider text-white/80">Pay with</p>
              <p className="font-display font-extrabold text-3xl sm:text-4xl leading-tight text-white break-words drop-shadow-sm">{brand.name}</p>
            </div>
          </div>
          <span className="text-xs font-bold px-3 py-1.5 rounded-full shrink-0 bg-white/20 text-white border border-white/30 backdrop-blur-sm">
            {method.account_type || "Send Money"}
          </span>
        </div>

        <div className="mt-5 rounded-xl p-4 bg-white/15 backdrop-blur-sm border border-white/25">
          <p className="text-xs font-bold uppercase tracking-wider text-white/85">Merchant Number</p>
          <div className="mt-1 flex items-center justify-between gap-3 flex-wrap">
            <p className="font-mono text-2xl sm:text-3xl font-extrabold tracking-wider text-white break-all min-w-0 drop-shadow-sm">{method.account_number}</p>
            <button
              type="button" onClick={onCopy}
              className="shrink-0 inline-flex items-center gap-1.5 text-sm font-bold px-3 py-2 rounded-lg bg-white text-slate-900 hover:bg-white/90 transition-colors shadow-sm"
            >
              {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
            </button>
          </div>
          {method.account_name && (
            <p className="mt-1 text-sm font-semibold text-white/90 break-words">{method.account_name}</p>
          )}
        </div>

        {(method.bank_name || method.branch_name || method.routing_number) && (
          <div className="mt-3 rounded-xl border border-white/25 bg-white/10 backdrop-blur-sm p-3 sm:p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-white/80 mb-2">ব্যাংক ডিটেইলস</p>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
              {method.bank_name && (
                <div className="min-w-0">
                  <dt className="text-[11px] uppercase tracking-wide text-white/70">Bank Name</dt>
                  <dd className="font-bold text-white break-words">{method.bank_name}</dd>
                </div>
              )}
              {method.branch_name && (
                <div className="min-w-0">
                  <dt className="text-[11px] uppercase tracking-wide text-white/70">Branch</dt>
                  <dd className="font-bold text-white break-words">{method.branch_name}</dd>
                </div>
              )}
              {method.routing_number && (
                <div className="min-w-0 sm:col-span-2">
                  <dt className="text-[11px] uppercase tracking-wide text-white/70">Routing Number</dt>
                  <dd className="font-mono font-bold text-white break-all">{method.routing_number}</dd>
                </div>
              )}
            </dl>
          </div>
        )}

        <ol className="mt-4 space-y-2">
          {brand.steps.map((step, i) => (
            <li key={i} className="flex items-start gap-3 text-sm font-semibold text-white/95">
              <span className="shrink-0 mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold bg-white/25 text-white border border-white/30">{i + 1}</span>
              <span className="leading-snug">{step}</span>
            </li>
          ))}
        </ol>

        {method.instructions && (
          <p className="mt-4 text-xs text-white/85 border-t border-white/25 pt-3">{method.instructions}</p>
        )}
      </div>
    </motion.div>
  );
}


function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-xs font-semibold text-slate-500">{label}</Label>
      {hint && <p className="text-[11px] text-slate-500 mt-0.5">{hint}</p>}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function Row({ icon, label, value, mono }: { icon: React.ReactNode; label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b last:border-0 border-slate-100">
      <div className="mt-0.5 text-slate-400 shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-slate-500">{label}</p>
        <p className={`text-sm font-semibold text-slate-900 break-words ${mono ? "font-mono break-all" : ""}`}>{value}</p>
      </div>
    </div>
  );
}

function Line({ label, value, bold, accent }: { label: string; value: string; bold?: boolean; accent?: string }) {
  return (
    <div className="flex justify-between items-baseline gap-3 text-sm">
      <span className={`${bold ? "font-semibold text-slate-900" : "text-slate-600"} min-w-0 break-words`}>{label}</span>
      <span className={`${bold ? "font-display font-bold text-base" : "font-mono text-slate-700"} shrink-0 whitespace-nowrap`} style={bold && accent ? { color: accent } : undefined}>{value}</span>
    </div>
  );
}
